// Shared logic for building the x-axis categories, series arrays, and y-axis
// config for a dashboard chart widget that may be comparing two series
// (different indicator/country/unit/frequency each) on the same chart,
// optionally on two separate Y-axes. Kept separate from SeriesWidget.jsx so
// the dual-series/dual-axis math has one place to live instead of drifting
// across copies.

const bucketGranularityFor = frequency => (frequency === 'Annual' ? 'Annual' : 'SubAnnual')

// Picking the finer of the two frequencies as the bucket grain is what lets
// e.g. annual GDP and monthly inflation share one timeline: the annual value
// lands once, at whichever month-bucket its own date falls in, while the
// monthly series gets one point per month as usual.
const combinedGranularity = (frequency1, frequency2) => (
    (bucketGranularityFor(frequency1) === 'Annual' && bucketGranularityFor(frequency2) === 'Annual')
        ? 'Annual'
        : 'SubAnnual'
)

const bucketKey = (dateStr, granularity) => (
    granularity === 'Annual' ? String(dateStr).slice(0, 4) : String(dateStr).slice(0, 7)
)

const bucketLabel = (key, granularity) => (
    granularity === 'Annual' ? key : `${key.slice(0, 4)} - ${key.slice(5, 7)}`
)

// Same technique used to combine multiple countries of one series (collect
// every row's date, de-dupe, map each country's values back onto that
// combined axis with null where it has no value for that date) — generalized
// here to a single "country group" so it can be called once per series.
function collectKeys(countryList, rows, keyFor) {
    const list = (Array.isArray(countryList) ? countryList : [])
        .filter(item => item.countryType === 'country' || item.countryType === 'region')
    const dataRows = Array.isArray(rows) ? rows : []

    const keys = []
    list.forEach(item => {
        dataRows
            .filter(row => row.country === item.country)
            .forEach(row => keys.push(keyFor(row.date)))
    })

    return { keys, list, dataRows }
}

const buildLines = (list, dataRows, rawCategories, keyFor) => (
    list.map(item => ({
        name: item.country,
        data: rawCategories.map(key => {
            const match = dataRows.find(row => row.country === item.country && keyFor(row.date) === key)
            return match ? match.value : null
        }),
        type: item.type,
        color: item.color,
    }))
)

/**
 * @param primary {{countryList, rows, frequency, unitLabel}}
 * @param secondary {{countryList, rows, frequency, unitLabel}|null}
 * @param useSecondaryAxis boolean
 * @param yDomain {{min, max}} optional fixed primary-axis bounds (as already used today)
 * @returns {{ categories: string[], rawCategories: string[], series: Array, yaxis: object|object[] }}
 */
export function buildComparisonSeriesData(primary, secondary, useSecondaryAxis, yDomain = {}) {
    const hasSecondary = Boolean(secondary && Array.isArray(secondary.rows) && secondary.rows.length > 0
        && Array.isArray(secondary.countryList) && secondary.countryList.length > 0)

    const granularity = hasSecondary
        ? combinedGranularity(primary.frequency, secondary.frequency)
        : bucketGranularityFor(primary.frequency)
    const keyFor = date => bucketKey(date, granularity)

    const primaryCollected = collectKeys(primary.countryList, primary.rows, keyFor)

    if (!hasSecondary) {
        const rawCategories = [...new Set(primaryCollected.keys)].sort()
        const categories = rawCategories.map(key => bucketLabel(key, granularity))
        const series = buildLines(primaryCollected.list, primaryCollected.dataRows, rawCategories, keyFor)

        return {
            categories,
            rawCategories,
            series,
            yaxis: {
                min: yDomain.min,
                max: yDomain.max,
                title: { text: primary.unitLabel || '' },
            },
        }
    }

    const secondaryCollected = collectKeys(secondary.countryList, secondary.rows, keyFor)
    const rawCategories = [...new Set([...primaryCollected.keys, ...secondaryCollected.keys])].sort()
    const categories = rawCategories.map(key => bucketLabel(key, granularity))

    const primarySeries = buildLines(primaryCollected.list, primaryCollected.dataRows, rawCategories, keyFor)
    const secondarySeries = buildLines(secondaryCollected.list, secondaryCollected.dataRows, rawCategories, keyFor)
    const series = [...primarySeries, ...secondarySeries]

    if (!useSecondaryAxis) {
        return {
            categories,
            rawCategories,
            series,
            yaxis: {
                min: yDomain.min,
                max: yDomain.max,
                title: { text: primary.unitLabel || '' },
            },
        }
    }

    // ApexCharts maps `yaxis[i]` to `series[i]` positionally when `yaxis` is
    // an array, so it must be the same length as `series` — only the first
    // entry of each axis "group" is shown, the rest are hidden duplicates
    // that keep the index alignment without drawing extra axis lines.
    const yaxis = series.map((_, index) => {
        const isSecondaryGroup = index >= primarySeries.length
        if (!isSecondaryGroup) {
            return {
                show: index === 0,
                min: yDomain.min,
                max: yDomain.max,
                title: { text: index === 0 ? (primary.unitLabel || '') : undefined },
            }
        }
        return {
            show: index === primarySeries.length,
            opposite: true,
            title: { text: index === primarySeries.length ? (secondary.unitLabel || '') : undefined },
        }
    })

    return { categories, rawCategories, series, yaxis }
}

// `ratio >= 5` (or the inverse, `<= 0.2`) between the two series' largest
// absolute values is the heuristic for auto-suggesting a secondary axis —
// always overridable by the user, never recomputed once they've clicked it.
export function suggestSecondaryAxis(primaryValues, secondaryValues) {
    const clean = values => (values || [])
        .filter(value => value !== null && value !== undefined && !Number.isNaN(Number(value)))
        .map(Number)
        .map(Math.abs)
    const primaryAbs = clean(primaryValues)
    const secondaryAbs = clean(secondaryValues)
    if (primaryAbs.length === 0 || secondaryAbs.length === 0) return false

    const primaryMax = Math.max(...primaryAbs)
    const secondaryMax = Math.max(...secondaryAbs)
    if (primaryMax === 0 || secondaryMax === 0) return false

    const ratio = primaryMax / secondaryMax
    return ratio >= 5 || ratio <= 0.2
}
